import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiRequestError, AUTH_EXPIRED_EVENT } from './api'
import { AuthSyncCancelledError, configureSupabase, getInitialSupabaseSession, logoutSupabase, resumeSupabaseSync, subscribeToSupabaseAuth, syncSupabaseSession } from './auth'
import type { AuthProvider, User } from './types'
import { AppShell, type Route } from './components/AppShell'
import { LoginView, OfflineState, SetupView } from './views/AuthViews'
import { ReviewView } from './views/ReviewView'
import { CalendarView } from './views/CalendarView'
import { AssetLibraryView } from './views/AssetLibraryView'
import { EditorQueueView } from './views/EditorQueueView'
import { FinalReviewView } from './views/FinalReviewView'
import { SourcesView } from './views/SourcesView'
import { ActivityView } from './views/ActivityView'
import { SettingsView } from './views/SettingsView'
import { MotionAssistant } from './components/MotionAssistant'
import { HomeView } from './views/HomeView'
import { ProjectsView } from './views/ProjectsView'
import { PersistentView } from './viewActivity'

type AuthState = 'loading' | 'login' | 'setup' | 'ready' | 'offline'
type NavigationLocation = { route: Route; projectId: string | null; clipId: string | null; sourceId: string | null }
const initialLocation: NavigationLocation = { route: 'home', projectId: null, clipId: null, sourceId: null }

export default function App() {
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [serverSession, setServerSession] = useState(false)
  const [authProvider, setAuthProvider] = useState<AuthProvider>('local')
  const [authError, setAuthError] = useState<string | null>(null)
  const [route, setRoute] = useState<Route>('home')
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [sourceFocus, setSourceFocus] = useState<string | null>(null)
  const [projectFocus, setProjectFocus] = useState<string | null>(null)
  const [clipFocus, setClipFocus] = useState<string | null>(null)
  const [clipFocusSequence, setClipFocusSequence] = useState(0)
  const [homeEntrySequence, setHomeEntrySequence] = useState(0)
  const [currentClipId, setCurrentClipId] = useState<string | null>(null)
  const [currentFinalClipId, setCurrentFinalClipId] = useState<string | null>(null)
  const navigationRef = useRef({ entries: [initialLocation], index: 0 })
  const [navigation, setNavigation] = useState(navigationRef.current)
  const supabaseUnsubscribe = useRef<(() => void) | null>(null)
  const authCheckInFlight = useRef<Promise<void> | null>(null)
  const authCheckGeneration = useRef(0)
  // Log Out revokes the session while the views are still mounted and
  // polling. The 401s their reads then receive are the sign-out the user
  // asked for, not an expired session, until someone signs in again.
  const signingOut = useRef(false)
  const describeAuthError = useCallback((error: unknown) => {
    if (error instanceof ApiRequestError && (error.status === 401 || error.status === 403)) return 'This email is not approved for Motion. Ask an administrator to add it in Settings, then request a new code.'
    if (error instanceof Error && error.message) return error.message
    return 'Motion could not verify this sign-in. Request a new code and try again.'
  }, [])
  const handleSyncedUser = useCallback((next: User) => {
    signingOut.current = false
    setUser(next)
    setAuthError(null)
    setAuthState('ready')
  }, [])
  const handleAuthSyncError = useCallback((error: unknown) => {
    authCheckGeneration.current += 1
    authCheckInFlight.current = null
    setAuthError(describeAuthError(error))
    void (async () => { try { await logoutSupabase() } finally { await api.logout().catch(() => undefined) } })()
    setUser(null)
    setAuthState('login')
  }, [describeAuthError])
  const handleSignedOut = useCallback((automatic: boolean) => {
    authCheckGeneration.current += 1
    authCheckInFlight.current = null
    setUser(null)
    // A direct-Supabase sync failure signs the provider out to clear its
    // invalid token. Preserve the useful error through that callback;
    // explicit logout clears it in its own finally block below.
    if (automatic) setAuthError('Your Motion session ended. Request a new email code to continue.')
    setAuthState('login')
  }, [])
  const checkAuth = useCallback(() => {
    if (authCheckInFlight.current) return authCheckInFlight.current
    const generation = ++authCheckGeneration.current
    const isCurrent = () => authCheckGeneration.current === generation
    const run = (async () => {
      setAuthState('loading')
      setAuthError(null)
      try {
        const initial = await api.bootstrap()
        if (!isCurrent()) return
        const provider = initial.auth?.provider || 'local'
        setAuthProvider(provider)
        setServerSession(Boolean(initial.auth?.server_session))
        if (initial.auth?.server_session) localStorage.removeItem('motion-summit-auth')
        let data = initial
        if (provider === 'supabase' && !initial.auth?.server_session) {
          if (!initial.auth) throw new Error('Supabase authentication configuration is missing.')
          configureSupabase(initial.auth)
          resumeSupabaseSync()
          if (!supabaseUnsubscribe.current) supabaseUnsubscribe.current = subscribeToSupabaseAuth(handleSyncedUser, handleAuthSyncError, handleSignedOut)
          const session = await getInitialSupabaseSession()
          if (!isCurrent()) return
          if (session) {
            try { await syncSupabaseSession(session) } catch (error) {
              if (error instanceof AuthSyncCancelledError || !isCurrent()) return
              setAuthError(describeAuthError(error))
              try { await logoutSupabase() } finally { await api.logout().catch(() => undefined) }
            }
          } else if (initial.user) {
            await api.logout().catch(() => undefined)
          }
          if (!isCurrent()) return
          data = await api.bootstrap()
        }
        if (!isCurrent()) return
        if (provider === 'local' && data.setup_required) { setAuthState('setup'); return }
        if (data.user) { signingOut.current = false; setUser(data.user); setAuthState('ready'); return }
        setAuthState('login')
      } catch (err) {
        if (!isCurrent() || err instanceof AuthSyncCancelledError) return
        if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
          handleSignedOut(true)
          return
        }
        setAuthState('offline')
      }
    })()
    authCheckInFlight.current = run
    void run.finally(() => { if (authCheckInFlight.current === run) authCheckInFlight.current = null })
    return run
  }, [describeAuthError, handleAuthSyncError, handleSignedOut, handleSyncedUser])
  useEffect(() => { void checkAuth() }, [checkAuth])
  useEffect(() => {
    const expire = () => { if (!signingOut.current) handleSignedOut(true) }
    window.addEventListener(AUTH_EXPIRED_EVENT, expire)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expire)
  }, [handleSignedOut])
  useEffect(() => () => { supabaseUnsubscribe.current?.(); supabaseUnsubscribe.current = null }, [])
  async function logout() {
    signingOut.current = true
    authCheckGeneration.current += 1
    authCheckInFlight.current = null
    if (serverSession) {
      try { await api.logout() }
      catch (error) { signingOut.current = false; throw error }
      handleSignedOut(false)
      setAuthError(null)
      return
    }
    setAuthState('loading')
    try {
      if (authProvider === 'supabase') {
        try { await logoutSupabase() } finally { await api.logout().catch(() => undefined) }
      } else {
        await api.logout().catch(() => undefined)
      }
    } finally {
      setUser(null)
      setAuthError(null)
      setAuthState('login')
    }
  }
  function showLocation(location: NavigationLocation) {
    if (location.route === 'home') setHomeEntrySequence(sequence => sequence + 1)
    setNotificationsOpen(false)
    setRoute(location.route)
    setProjectFocus(location.projectId)
    setClipFocus(location.clipId)
    if (['review', 'final', 'editor'].includes(location.route) && location.clipId) setClipFocusSequence(sequence => sequence + 1)
    setSourceFocus(location.sourceId)
  }
  function navigate(location: NavigationLocation) {
    const previous = navigationRef.current
    const current = previous.entries[previous.index]
    if (current.route === location.route && current.projectId === location.projectId && current.clipId === location.clipId && current.sourceId === location.sourceId) {
      // Reapply explicit Open requests and close notifications even when the
      // destination is already current, without adding duplicate history.
      showLocation(location)
      return
    }
    const next = { entries: [...previous.entries.slice(0, previous.index + 1), location], index: previous.index + 1 }
    navigationRef.current = next
    setNavigation(next)
    showLocation(location)
  }
  function moveHistory(direction: -1 | 1) {
    const previous = navigationRef.current
    const index = previous.index + direction
    if (index < 0 || index >= previous.entries.length) return
    const next = { ...previous, index }
    navigationRef.current = next
    setNavigation(next)
    showLocation(next.entries[index])
  }
  function goToSource(id?: string) { navigate({ route: 'sources', projectId: null, clipId: null, sourceId: id || null }) }
  if (authState === 'loading') return <div className="auth-page workspace-loading" role="status" aria-label="Loading Motion workspace"><span className="motion-logo workspace-loading-logo" aria-hidden="true" /><span className="sr-only">Loading Motion workspace</span></div>
  if (authState === 'setup') return <SetupView onComplete={() => void checkAuth()} />
  if (authState === 'offline') return <OfflineState onRetry={() => void checkAuth()} />
  if (authState === 'login') return <LoginView serverSession={serverSession} provider={authProvider} initialError={authError} onComplete={() => void checkAuth()} />
  if (!user) return null
  const canIngest = true
  return <AppShell user={user} route={route} canGoBack={navigation.index > 0} canGoForward={navigation.index < navigation.entries.length - 1} onBack={() => moveHistory(-1)} onForward={() => moveHistory(1)} onLogout={logout} notificationsOpen={notificationsOpen} onNotificationsToggle={() => setNotificationsOpen((open) => !open)} onRoute={(next) => navigate({ route: next === 'calendar' && !canIngest ? 'review' : next, projectId: null, clipId: null, sourceId: null })}>
    <PersistentView key={`home-${user.id}`} active={route === 'home'}><HomeView entrySequence={homeEntrySequence} userId={user.id} canIngest={canIngest} onReview={() => navigate({ route: 'review', projectId: null, clipId: null, sourceId: null })} onClip={(id) => navigate({ route: 'review', projectId: null, clipId: id, sourceId: null })} /></PersistentView>
    <PersistentView key={`review-${user.id}`} active={route === 'review'}><ReviewView user={user} focusClipId={route === 'review' ? clipFocus : null} focusClipSequence={clipFocusSequence} onSelectedClip={setCurrentClipId} /></PersistentView>
    {canIngest ? <PersistentView key={`calendar-${user.id}`} active={route === 'calendar'}><CalendarView user={user} focusClipId={clipFocus} onClip={id => navigate({ route: 'calendar', projectId: null, clipId: id, sourceId: null })} /></PersistentView> : null}
    <PersistentView key={`editor-${user.id}`} active={route === 'editor'}><EditorQueueView user={user} focusClipId={route === 'editor' ? clipFocus : null} focusClipSequence={clipFocusSequence} /></PersistentView>
    <PersistentView key={`final-${user.id}`} active={route === 'final'}><FinalReviewView user={user} focusClipId={route === 'final' ? clipFocus : null} focusClipSequence={clipFocusSequence} onSelectedClip={setCurrentFinalClipId} onEditorQueue={() => navigate({ route: 'editor', projectId: null, clipId: null, sourceId: null })} /></PersistentView>
    <PersistentView key={`projects-${user.id}`} active={route === 'projects'}><ProjectsView initialProjectId={projectFocus} onProjectChange={(id) => navigate({ route: 'projects', projectId: id, clipId: null, sourceId: null })} canManage={canIngest} onClip={(id) => navigate({ route: 'review', projectId: null, clipId: id, sourceId: null })} /></PersistentView>
    <PersistentView key={`library-${user.id}`} active={route === 'library'}><AssetLibraryView onClip={id => navigate({ route: 'calendar', projectId: null, clipId: id, sourceId: null })} /></PersistentView>
    <PersistentView key={`sources-${user.id}`} active={route === 'sources'}><SourcesView userId={user.id} onProject={(id) => navigate({ route: 'projects', projectId: id, clipId: null, sourceId: null })} canIngest={canIngest} focusSourceId={sourceFocus} /></PersistentView>
    <PersistentView key={`settings-${user.id}`} active={route === 'settings'}><SettingsView user={user} authProvider={authProvider} /></PersistentView>
    {notificationsOpen ? <><button type="button" className="notifications-dismiss-layer" aria-label="Close notifications" onClick={() => setNotificationsOpen(false)} /><ActivityView onClose={() => setNotificationsOpen(false)} onOpen={navigate} /></> : null}
    <MotionAssistant onSource={goToSource} route={route} currentClipId={route === 'review' ? currentClipId : route === 'final' ? currentFinalClipId : null} onRoute={(next) => navigate({ route: next === 'calendar' && !canIngest ? 'review' : next, projectId: null, clipId: null, sourceId: null })} onClip={(id) => navigate({ route: 'review', projectId: null, clipId: id, sourceId: null })} />
  </AppShell>
}
